import React, { useEffect, useState, useCallback } from "react";
import { Btn, Eyebrow } from "../../components/Primitives";
import { api } from "../../lib/api";
import logger from "../../lib/logger";
import AvailabilityPicker from "./AvailabilityPicker";
import VenuePicker from "./VenuePicker";
import Briefing from "./Briefing";
import Checkin from "./Checkin";
import MessageThread from "./MessageThread";

const SLOT_LABELS = {
    afternoon: "Afternoon (12-3pm)",
    early_eve: "Early evening (5-8pm)",
    evening: "Evening (7-10pm)",
};

function fmtDateLong(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
}

function fmtCountdown(expiresAt) {
    if (!expiresAt) return null;
    const diff = new Date(expiresAt).getTime() - Date.now();
    if (diff <= 0) return "Expired";
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    if (days > 0) return `${days}d ${hours}h left to plan`;
    return `${hours}h left to plan`;
}

export default function MatchDetail({ user, matchId, onBack }) {
    const [match, setMatch] = useState(null);
    const [loading, setLoading] = useState(true);
    const [phase, setPhase] = useState("hub"); // hub | availability | venue | briefing | checkin | messages
    const [overlap, setOverlap] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    const load = useCallback(async () => {
        try {
            const m = await api.dating.getMatch(matchId, user.session_id);
            setMatch(m);
            // hydrate overlap from existing proposals
            const myProp = (m.proposals || []).find(p => p.proposed_by_user_id === user.session_id);
            const theirProp = (m.proposals || []).find(p => p.proposed_by_user_id !== user.session_id);
            if (myProp && theirProp) {
                const keysA = new Set(myProp.time_windows.map(w => `${w.date}|${w.slot}`));
                const overlapWins = theirProp.time_windows.filter(w => keysA.has(`${w.date}|${w.slot}`));
                setOverlap(overlapWins);
            }
        } catch (e) {
            logger.error("match load failed", e);
        }
        setLoading(false);
    }, [matchId, user.session_id]);

    useEffect(() => { load(); }, [load]);

    const submitAvailability = async (windows) => {
        setSubmitting(true);
        try {
            const r = await api.dating.submitAvailability(matchId, user.session_id, windows);
            if (r.both_submitted && r.overlap?.length) {
                setOverlap(r.overlap);
                setPhase("venue");
            } else {
                await load();
                setPhase("hub");
            }
        } catch (e) {
            logger.error("availability submit failed", e);
            alert(e?.response?.data?.detail || "Could not submit your windows.");
        }
        setSubmitting(false);
    };

    if (loading) {
        return (
            <div className="flex-1 flex flex-col bg-cream items-center justify-center">
                <div className="text-ink-muted text-sm">Loading match…</div>
            </div>
        );
    }
    if (!match) {
        return (
            <div className="flex-1 flex flex-col bg-cream items-center justify-center px-8">
                <p className="font-display italic text-xl text-burgundy mb-3">We couldn't find this match.</p>
                <Btn variant="secondary" onClick={onBack}>Back to matches</Btn>
            </div>
        );
    }

    // Sub-phases render full-screen
    if (phase === "availability") {
        return (
            <AvailabilityPicker
                onSubmit={submitAvailability}
                submitting={submitting}
                onCancel={() => setPhase("hub")}
            />
        );
    }
    if (phase === "venue" && overlap.length > 0) {
        return (
            <VenuePicker
                user={user}
                matchId={matchId}
                overlap={overlap}
                onProposed={async () => { await load(); setPhase("hub"); }}
                onCancel={() => setPhase("hub")}
            />
        );
    }
    if (phase === "briefing" && match.active_date) {
        return <Briefing user={user} dateId={match.active_date.id} onBack={() => setPhase("hub")} />;
    }
    if (phase === "checkin" && match.active_date) {
        return <Checkin user={user} dateId={match.active_date.id} onDone={async () => { await load(); setPhase("hub"); }} onBack={() => setPhase("hub")} />;
    }
    if (phase === "messages") {
        return <MessageThread user={user} matchId={matchId} onBack={() => setPhase("hub")} onPlan={() => setPhase("availability")} />;
    }

    // Main hub
    const proposals = match.proposals || [];
    const mineSubmitted = proposals.some(p => p.proposed_by_user_id === user.session_id);
    const theirsSubmitted = proposals.some(p => p.proposed_by_user_id !== user.session_id);
    const activeDate = match.active_date;
    const partner = match.other_user || {};
    const partnerArch = match.other_archetype;
    const myArch = match.self_archetype;

    return (
        <div className="flex-1 flex flex-col bg-cream overflow-y-auto" data-testid="match-detail-screen">
            <div className="px-6 pt-6 pb-2 flex items-center justify-between">
                <button data-testid="match-back-btn" onClick={onBack} className="text-[11px] tracking-[0.28em] uppercase text-ink-muted">← Matches</button>
                {match.status === "planning" && (
                    <span className="text-[10px] tracking-[0.32em] uppercase text-gold-deep" data-testid="match-countdown">
                        {fmtCountdown(match.expires_at)}
                    </span>
                )}
            </div>

            <div className="px-7 pt-4 pb-2">
                <Eyebrow>Your match</Eyebrow>
                <h1 className="mt-2 font-display text-4xl text-burgundy leading-[1.05]">
                    <span className="italic">{partner.first_name || "Your match"}.</span>
                </h1>
                {partnerArch && (
                    <p className="mt-1 text-xs tracking-[0.24em] uppercase text-ink-muted">
                        {partnerArch.name}
                    </p>
                )}
            </div>

            <div className="px-7 py-4">
                <div className="border border-burgundy/15 bg-white p-5">
                    <Eyebrow>Why we paired you</Eyebrow>
                    {myArch && partnerArch && (
                        <div className="mt-2 text-[11px] tracking-[0.24em] uppercase text-ink-muted">
                            {myArch.name} × {partnerArch.name}
                        </div>
                    )}
                    <p className="mt-3 text-base text-ink leading-relaxed" data-testid="pair-insight">
                        {match.archetype_pair_insight}
                    </p>
                </div>
            </div>

            {/* State machine UI */}
            <div className="px-6 pb-8 space-y-3">

                {match.status === "dissolved" && (
                    <div className="border border-burgundy/15 bg-white p-5" data-testid="match-dissolved">
                        <Eyebrow>Match expired</Eyebrow>
                        <p className="mt-2 text-sm text-ink-muted">This match wasn't planned within 7 days and has been dissolved.</p>
                    </div>
                )}

                {activeDate && activeDate.status === "confirmed" && (
                    <div className="border border-burgundy/15 bg-white p-5" data-testid="active-date-confirmed">
                        <Eyebrow>Date confirmed</Eyebrow>
                        <h3 className="mt-2 font-display text-xl text-burgundy">
                            {fmtDateLong(activeDate.scheduled_at)}
                        </h3>
                        <p className="mt-1 text-xs tracking-[0.18em] uppercase text-ink-muted">
                            {SLOT_LABELS[activeDate.scheduled_window?.slot]}
                        </p>
                        {activeDate.venue && (
                            <>
                                <div className="mt-4 font-display text-lg text-burgundy">{activeDate.venue.name}</div>
                                <div className="text-xs text-ink-muted">{activeDate.venue.neighborhood} · {activeDate.venue.address}</div>
                                <p className="mt-2 text-sm text-ink leading-snug">{activeDate.venue.one_line_pitch}</p>
                            </>
                        )}
                        <div className="mt-4 space-y-2">
                            <Btn data-testid="open-briefing" variant="primary" onClick={() => setPhase("briefing")}>
                                Tell me about {partner.first_name || "them"}
                            </Btn>
                            <a
                                data-testid="download-ics"
                                href={api.dating.icsUrl(activeDate.id, user.session_id)}
                                download
                                className="block w-full text-center font-bold tracking-[0.16em] uppercase text-xs py-4 px-6 bg-transparent border border-burgundy text-burgundy hover:bg-cream-soft"
                            >
                                Add to calendar
                            </a>
                            <Btn data-testid="open-checkin" variant="secondary" onClick={() => setPhase("checkin")}>
                                Already met? Check in
                            </Btn>
                        </div>
                    </div>
                )}

                {activeDate && activeDate.status === "proposed" && (
                    <div className="border border-burgundy/15 bg-white p-5" data-testid="active-date-proposed">
                        <Eyebrow>Proposal in flight</Eyebrow>
                        <h3 className="mt-2 font-display text-xl text-burgundy">
                            {fmtDateLong(activeDate.scheduled_at)}
                        </h3>
                        <p className="mt-1 text-xs tracking-[0.18em] uppercase text-ink-muted">
                            {SLOT_LABELS[activeDate.scheduled_window?.slot]}
                        </p>
                        {activeDate.venue && (
                            <div className="mt-2 text-sm text-ink-muted">at {activeDate.venue.name}</div>
                        )}
                        <p className="mt-3 text-sm text-ink-muted">
                            {activeDate.proposed_by_user_id === user.session_id
                                ? `Waiting on ${partner.first_name || "your match"} to confirm.`
                                : `${partner.first_name || "Your match"} proposed this. Want to confirm?`}
                        </p>
                        {activeDate.proposed_by_user_id !== user.session_id && (
                            <Btn
                                data-testid="confirm-date-btn"
                                variant="primary"
                                className="mt-4"
                                onClick={async () => {
                                    try {
                                        await api.dating.confirmDate(matchId, user.session_id);
                                        await load();
                                    } catch (e) { logger.error("confirm failed", e); }
                                }}
                            >Confirm this date</Btn>
                        )}
                    </div>
                )}

                {match.status === "completed" && (
                    <div className="border border-burgundy/15 bg-white p-5" data-testid="match-completed">
                        <Eyebrow>Match complete</Eyebrow>
                        <p className="mt-2 text-sm text-ink-muted">Your check-in has been recorded. Thank you.</p>
                    </div>
                )}

                {match.status === "planning" && !activeDate && (
                    <div className="border border-burgundy/15 bg-white p-5" data-testid="planning-card">
                        <Eyebrow>Plan a date</Eyebrow>
                        <h3 className="mt-2 font-display text-xl text-burgundy">
                            Three windows. <span className="italic">Then a venue.</span>
                        </h3>
                        <p className="mt-2 text-sm text-ink-muted leading-snug">
                            {mineSubmitted && theirsSubmitted
                                ? "You both submitted. Pick from the overlap."
                                : mineSubmitted
                                    ? `Waiting on ${partner.first_name || "your match"} to share their windows.`
                                    : "Pick three windows in the next 14 days that work for you."}
                        </p>
                        <div className="mt-4 space-y-2">
                            {!mineSubmitted && (
                                <Btn data-testid="start-availability-btn" variant="primary" onClick={() => setPhase("availability")}>
                                    Pick my 3 windows
                                </Btn>
                            )}
                            {mineSubmitted && theirsSubmitted && overlap.length > 0 && (
                                <Btn data-testid="pick-venue-btn" variant="primary" onClick={() => setPhase("venue")}>
                                    Pick a venue ({overlap.length} window{overlap.length === 1 ? "" : "s"} overlap)
                                </Btn>
                            )}
                            {mineSubmitted && theirsSubmitted && overlap.length === 0 && (
                                <p className="text-xs text-burgundy">
                                    No overlap with their windows yet — try picking different times.
                                </p>
                            )}
                            <Btn data-testid="open-messages-btn" variant="secondary" onClick={() => setPhase("messages")}>
                                Send a short message
                            </Btn>
                        </div>
                    </div>
                )}

                {/* Always-available secondary actions */}
                {(match.status === "booked" || match.status === "completed") && (
                    <Btn data-testid="open-messages-btn-secondary" variant="secondary" onClick={() => setPhase("messages")}>
                        Messages
                    </Btn>
                )}
            </div>
        </div>
    );
}
